/**
 * BienvenidaPortal — popup de primer ingreso al portal renovado (SOA 10 PRO · brand book §15h).
 *
 * Cuatro pasos: 1 renovamos tu portal · 2 lo nuevo · 3 llevalo en tu celular · 4 listo (con el CTA según haya piezas
 * esperando el ok). Modal de 960×600 en escritorio; hoja inferior en celular.
 *
 * Reglas:
 *   - Una vez por persona (se guarda en el servidor, no por dispositivo). Saltar cuenta como visto.
 *   - Queda disponible en Cuenta › «Lo nuevo del portal» (evento `abrir-bienvenida-portal`); abrirla a mano no guarda nada.
 *   - No aparece si la persona entra por un link de aprobación o de compartir: primero aprueba; sale la próxima vez.
 *   - Convive con los demás avisos por turnos (InterruptBudget, prioridad 4 = la primera) y reemplaza al tour viejo.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useInterruptSlot } from "../context/InterruptBudget.jsx";
import Icon from "./Icon.jsx";

export const EVENTO_ABRIR_BIENVENIDA_PORTAL = "abrir-bienvenida-portal";
const PETROLEO = "#0E7490", NOCHE = "#0B0D10";

const NOVEDADES = [
  { ic: "task_alt", t: "Esperan tu ok" }, { ic: "view_week", t: "Tu semana" }, { ic: "donut_large", t: "Tu plan del mes" }, { ic: "photo_camera", t: "Fotos del evento, el mismo día" },
];
const primerNombre = (n) => String(n || "").trim().split(/\s+/)[0] || "";

export default function BienvenidaPortal() {
  const { user, clienteActivo, clienteActivoId } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { granted, request, release } = useInterruptSlot("bienvenida", 4);
  const [pendiente, setPendiente] = useState(false);   // toca mostrarla (aún no la vio)
  const [manual, setManual] = useState(false);          // abierta desde el menú
  const [paso, setPaso] = useState(0);
  const [pend, setPend] = useState(0);                  // piezas esperando el ok

  // ¿Ya la vio? Una sola vez por persona. No se evalúa si entró por un link de aprobación.
  useEffect(() => {
    if (!user || !clienteActivoId) return;
    if (/^\/(share|verify|v\/)/.test(pathname) || /\/aprobar/.test(pathname)) return;
    let vivo = true;
    api.bienvenidaStatus().then(r => { if (vivo && !r?.visto) setPendiente(true); }).catch(() => {});
    return () => { vivo = false; };
  }, [user?.id, clienteActivoId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const abrir = () => { setPaso(0); setManual(true); };
    window.addEventListener(EVENTO_ABRIR_BIENVENIDA_PORTAL, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_BIENVENIDA_PORTAL, abrir);
  }, []);

  const visible = manual || (pendiente && granted);
  useEffect(() => { if (pendiente) request(); }, [pendiente, request]);
  useEffect(() => {
    if (!visible || !clienteActivoId) return;
    api.dashboard(clienteActivoId).then(d => setPend(Number(d?.kpis?.por_aprobar) || 0)).catch(() => setPend(0));
  }, [visible, clienteActivoId]);

  const cerrar = useCallback(async (modo) => {
    if (!manual) {
      try { await api.bienvenidaVisto(modo, paso + 1); } catch { /* se vuelve a ofrecer la próxima vez */ }
      setPendiente(false); release();
      window.dispatchEvent(new Event("portal-onboarding-completado"));   // la bienvenida reemplaza al tour viejo
    }
    setManual(false);
    if (modo === "cta") navigate(pend > 0 ? "/tareas/aprobar" : "/");
  }, [manual, paso, pend, navigate, release]);

  useEffect(() => {
    if (!visible) return;
    const tecla = (e) => {
      if (e.key === "ArrowRight") setPaso(p => Math.min(3, p + 1));
      else if (e.key === "ArrowLeft") setPaso(p => Math.max(0, p - 1));
      else if (e.key === "Escape") cerrar("saltar");
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [visible, cerrar]);

  if (!visible) return null;

  const nombre = primerNombre(user?.nombre), cliente = clienteActivo?.nombre || "tu marca";
  const PASOS = [
    { t: `Hola${nombre ? `, ${nombre}` : ""}. Renovamos tu portal.`, d: "Es el mismo lugar de siempre, más claro y más rápido. Te mostramos lo nuevo en un minuto.", tm: `Hola${nombre ? `, ${nombre}` : ""}. Renovamos tu portal.`, dm: "Más claro y más rápido. Te mostramos lo nuevo." },
    { t: `Lo nuevo para ${cliente}`, d: "Cuatro cosas que ahora ves apenas entrás.", tm: "Lo nuevo", dm: "Cuatro cosas que ves apenas entrás." },
    { t: "Llevalo en tu celular", d: "El portal se instala como una app. Aprobás desde donde estés, sin buscar el link.", tm: "Llevalo en tu celular", dm: "Aprobás desde donde estés." },
    { t: "Listo", d: pend > 0 ? `Tenés ${pend} ${pend === 1 ? "pieza esperando" : "piezas esperando"} tu ok. Empezá por ahí.` : "Está todo al día. Ya podés recorrer tu portal.", tm: "Listo", dm: pend > 0 ? `Tenés ${pend} ${pend === 1 ? "pieza" : "piezas"} esperando tu ok.` : "Está todo al día." },
  ];
  const LISTAS = [
    [{ ic: "lock_open", t: "Tu acceso sigue igual", d: "Mismo mail y misma contraseña. Ahora también podés entrar con un link." }, { ic: "folder_open", t: "Todo tu historial está", d: "Piezas, aprobaciones, reportes y facturas anteriores." }, { ic: "support_agent", t: "Tu ejecutiva, la misma", d: "Sigue a cargo de tu cuenta." }],
    [{ ic: "task_alt", t: "Esperan tu ok, primero", d: "Aprobás o comentás cada pieza con un toque." }, { ic: "view_week", t: "Tu semana en 30 segundos", d: "Qué se publicó, qué sale y qué falta." }, { ic: "donut_large", t: "Tu plan del mes", d: "Cuánto usamos de lo contratado y qué queda." }],
    [{ ic: "ios_share", t: "Tocá Compartir en el navegador", d: "" }, { ic: "add_box", t: "Elegí «Agregar a inicio»", d: "" }, { ic: "notifications", t: "Permití los avisos", d: "Solo te avisamos cuando algo necesita tu ok." }],
    [{ ic: "chat_bubble", t: "¿Dudas? Escribile a tu ejecutiva", d: "Desde el botón de ayuda o por WhatsApp." }, { ic: "replay", t: "Volver a ver esto", d: "Cuenta › Lo nuevo del portal." }],
  ];
  const oscuro = paso === 0 || paso === 3;
  const ctaFinal = pend > 0 ? "Ver lo que espera mi ok" : "Ir a mi portal";
  const ultimo = paso === 3;
  const siguiente = () => (ultimo ? cerrar("cta") : setPaso(paso + 1));

  const lista = (
    <div className="flex flex-col gap-3.5">
      {(paso === 1 ? NOVEDADES.map(n => ({ ic: n.ic, t: n.t, d: "" })) : LISTAS[paso]).map(it => (
        <div key={it.t} className="flex items-start gap-3.5 rounded-[20px] bg-[#F4F5F7] p-4">
          <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-[14px] bg-[#CFFAFE] text-[#0E7490]"><Icon name={it.ic} size={24} /></span>
          <span className="flex flex-col"><span className="text-[17px] font-extrabold leading-tight text-[#111318]">{it.t}</span>{it.d && <span className="mt-0.5 text-[14px] leading-snug text-[#3B3E46]">{it.d}</span>}</span>
        </div>
      ))}
    </div>
  );
  const puntos = <div className="flex items-center gap-1.5" aria-hidden="true">{[0, 1, 2, 3].map(i => <span key={i} className="h-2 rounded" style={{ width: i === paso ? 28 : 8, background: i === paso ? PETROLEO : "#CDD0D7" }} />)}</div>;
  const botones = (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => setPaso(Math.max(0, paso - 1))} className="flex h-[52px] items-center rounded-2xl bg-[#F2F3F6] px-[18px] text-[16px] font-extrabold" style={{ visibility: paso === 0 ? "hidden" : "visible" }}>Atrás</button>
      <button type="button" onClick={siguiente} className="flex h-[52px] flex-1 items-center justify-center rounded-2xl text-[16px] font-extrabold text-white" style={{ background: PETROLEO }}>{ultimo ? ctaFinal : "Siguiente"}</button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center md:items-center" style={{ background: "rgba(11,13,16,0.55)" }} role="dialog" aria-modal="true" aria-labelledby="bienvenida-titulo">
      {/* Celular: hoja inferior */}
      <div className="flex max-h-[calc(100vh-90px)] w-full flex-col gap-5 overflow-y-auto rounded-t-[30px] bg-white p-6 pb-8 text-[#111318] md:hidden" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-between">{puntos}<button type="button" onClick={() => cerrar("saltar")} className="text-[15px] font-extrabold text-[#6B6F7A]">Saltar</button></div>
        <div className="flex flex-col gap-1.5"><h2 id="bienvenida-titulo" className="m-0 text-[28px] font-black leading-[1.05] tracking-[-0.02em]">{PASOS[paso].tm}</h2><p className="m-0 text-[16px] leading-snug text-[#3B3E46]">{PASOS[paso].dm}</p></div>
        {lista}
        {botones}
      </div>

      {/* Escritorio: 960 × 600 */}
      <div className="hidden h-[600px] w-[960px] max-w-[calc(100vw-48px)] overflow-hidden rounded-[36px] bg-white text-[#111318] md:grid" style={{ gridTemplateColumns: "420px 1fr", boxShadow: "0 40px 100px rgba(11,13,16,0.4)" }}>
        <div className="relative flex flex-col justify-center overflow-hidden px-10 py-11" style={{ background: oscuro ? NOCHE : "#ECFEFF", color: oscuro ? "#FFFFFF" : "#164E63" }}>
          <div aria-hidden="true" className="absolute" style={{ right: -120, top: -140, width: 380, height: 380, borderRadius: "50%", background: `radial-gradient(closest-side,${oscuro ? "rgba(34,211,238,0.35)" : "rgba(14,116,144,0.18)"},rgba(34,211,238,0))` }} />
          <span className="relative mb-4 text-[14px] font-extrabold" style={{ color: oscuro ? "#22D3EE" : PETROLEO }}>Paso {paso + 1} de 4</span>
          <h2 id="bienvenida-titulo-d" className="relative m-0 text-[38px] font-black leading-[1.04] tracking-[-0.03em] [text-wrap:balance]">{PASOS[paso].t}</h2>
          <p className="relative mt-4 text-[17px] leading-[1.5]" style={{ color: oscuro ? "#C7CAD1" : "#164E63" }}>{PASOS[paso].d}</p>
        </div>
        <div className="flex flex-col justify-between p-11">
          <div className="flex justify-end"><button type="button" onClick={() => cerrar("saltar")} className="text-[15px] font-extrabold text-[#6B6F7A]">Saltar</button></div>
          <div className="my-4">{lista}</div>
          <div className="flex flex-col gap-4">{puntos}{botones}</div>
        </div>
      </div>
    </div>
  );
}
